import { useEffect, useRef, useState } from 'react'
import { useGameStore } from '../stores/gameStore'
import { useSaveStore } from '../stores/saveStore'
import { useNavigationStore } from '../stores/navigationStore'
import { useShangGameStore } from '../stores/shangGameStore'
import { useFullscreen } from '../hooks/useFullscreen'
import { TopBar } from './TopBar'
import { SidePanel } from './SidePanel'
import { CityPanel } from './CityPanel'
import { TalentPanel } from './TalentPanel'
import { SaveManager } from './SaveManager'
import { FactionList } from './FactionList'
import { CityManagementPage } from '../pages/CityManagementPage'
import { InternalAffairsPage } from '../pages/InternalAffairsPage'
import { SHANG_FACTIONS } from '../data/shangDynastyData'
import './GameContainer.css'

export function GameContainer() {
  const gameStore = useGameStore()
  const { currentView, isPaused, togglePause, gameSpeed, setGameSpeed, tick } = gameStore
  const { autoSaveGame, initSession } = useSaveStore()
  const { setCurrentPage } = useNavigationStore()
  const { config } = useShangGameStore()
  const { isFullscreen, isSupported: isFullscreenSupported, toggleFullscreen } = useFullscreen()
  const [showSaveManager, setShowSaveManager] = useState(false)
  const animationRef = useRef<number>()
  const autoSaveRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // 获取当前玩家势力ID
  const playerFactionId = config?.playerFactionId || 'shang'

  // 返回大厅
  const handleBackToHall = () => {
    setCurrentPage('hall')
  }

  // 初始化游戏会话
  useEffect(() => {
    initSession()
  }, [initSession])

  // 游戏时间循环
  useEffect(() => {
    const gameLoop = () => {
      tick(0)
      animationRef.current = requestAnimationFrame(gameLoop)
    }

    animationRef.current = requestAnimationFrame(gameLoop)

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }
  }, [tick])

  // 自动存档
  useEffect(() => {
    // 每 30 秒自动存档
    autoSaveRef.current = setInterval(() => {
      const saveData = {
        currentView: gameStore.currentView,
        isPaused: gameStore.isPaused,
        gameSpeed: gameStore.gameSpeed,
        gameDate: gameStore.gameDate,
        resources: gameStore.resources,
        resourceIncome: gameStore.resourceIncome,
        buildings: gameStore.buildings,
        populationAllocation: gameStore.populationAllocation,
        talents: gameStore.talents,
        availableTalents: gameStore.availableTalents,
        recruitCooldown: gameStore.recruitCooldown,
      }
      autoSaveGame(saveData)
    }, 30000)

    return () => {
      if (autoSaveRef.current) {
        clearInterval(autoSaveRef.current)
      }
    }
  }, [autoSaveGame, gameStore])

  return (
    <div className="game-container">
      <TopBar />
      
      <div className="main-layout">
        <SidePanel />
        
        <div className="content-area">
          {currentView === 'map' && (
            <FactionList
              playerFactionId={playerFactionId}
              factions={SHANG_FACTIONS}
              onFactionSelect={(faction) => {
                console.log('选中势力:', faction.name)
              }}
            />
          )}
          {currentView === 'city' && <CityPanel />}
          {currentView === 'internal' && (
            <InternalAffairsPage
              factionId={playerFactionId}
            />
          )}
          {currentView === 'talent' && <TalentPanel />}
          {currentView === 'city-management' && (
            <CityManagementPage
              factionId={playerFactionId}
              onBack={() => gameStore.setView('city')}
            />
          )}
        </div>
      </div>

      <div className="bottom-bar">
        <div className="time-controls">
          <button 
            className={`control-btn ${isPaused ? 'active' : ''}`}
            onClick={togglePause}
          >
            {isPaused ? '▶ 继续' : '⏸ 暂停'}
          </button>
          <div className="speed-controls">
            {[1, 2, 4].map(speed => (
              <button
                key={speed}
                className={`speed-btn ${gameSpeed === speed ? 'active' : ''}`}
                onClick={() => setGameSpeed(speed)}
              >
                {speed}x
              </button>
            ))}
          </div>
        </div>
        <div className="game-status">
          {isPaused ? '游戏已暂停' : `游戏进行中 (${gameSpeed}x)`}
        </div>
        <div className="bottom-actions">
          {isFullscreenSupported && (
            <button
              className={`pill-btn fullscreen-btn ${isFullscreen ? 'active' : ''}`}
              onClick={toggleFullscreen}
              title={isFullscreen ? '退出全屏' : '全屏模式'}
            >
              <span className="btn-icon">⛶</span>
            </button>
          )}
          <button
            className="pill-btn back-btn"
            onClick={handleBackToHall}
          >
            <span className="btn-icon">←</span>
            <span className="btn-text">返回</span>
          </button>
          <button
            className="pill-btn save-btn"
            onClick={() => setShowSaveManager(true)}
          >
            <span className="btn-icon">💾</span>
            <span className="btn-text">存档</span>
          </button>
        </div>
      </div>

      <SaveManager
        isOpen={showSaveManager}
        onClose={() => setShowSaveManager(false)}
      />
    </div>
  )
}
